// src/utils/qrSchemas.js
const qrFieldLabels = {
  en: {
    business_name: "Business name",
    product_name: "Product name",
    discount: "Discount",
    promo_code: "Promo code",
    trial_days: "Trial days",
    terms: "Terms",
    service_type: "Service type",
    duration: "Duration",
    location: "Location",
    price: "Price",
    event_type: "Event type",
    event_date: "Event date",
    event_time: "Event time",
    description: "Description"
  },
  es: {
    business_name: "Nombre del negocio",
    product_name: "Producto",
    discount: "Descuento",
    promo_code: "Código promocional",
    trial_days: "Días de prueba",
    terms: "Términos",
    service_type: "Tipo de servicio",
    duration: "Duración",
    location: "Ubicación",
    price: "Precio",
    event_type: "Tipo de evento",
    event_date: "Fecha del evento",
    event_time: "Hora del evento",
    description: "Descripción"
  }
};

const qrSchemas = {
  storefront: {
    required: ["business_name"] // Targets client_id directly; no extra fields required
  },
  product: {
    required: ["product_name", "price"]
  },
  promo: {
    required: ["product_name", "discount", "promo_code"]
  },
  trial: {
    required: ["product_name", "trial_days", "terms"]
  },
  service: {
    required: ["service_type", "duration", "location"]
  },
  order: {
    required: ["product_name", "price", "service_type", "location"]
  },
  event: {
    required: ["event_type", "event_date", "event_time", "location"]
  },
  custom: {
    required: ["product_name", "description"]
  }
};

function getQrFieldLabel(field, lang = "en") {
  return qrFieldLabels[lang]?.[field] || qrFieldLabels.en[field] || field;
}

function validatePayload(qr_type, payload) {
  const schema = qrSchemas[qr_type];
  if (!schema) throw new Error("Unknown QR type");

  for (const field of schema.required) {
    if (
      payload[field] === undefined ||
      payload[field] === null ||
      String(payload[field]).trim() === ""
    ) {
      throw new Error(`Missing required field: ${field}`);
    }
  }

  return true;
}

function deriveQrIntent(qr_type, payload = {}, objective = "", title = "") {
  const p = payload || {};
  const objRaw = String(objective || "").trim();
  const obj = objRaw.toLowerCase();
  const product = String(p.product_name || p.plan_name || "").toLowerCase();
  const terms = String(p.terms || "").toLowerCase();

  const hasSubscriptionSignal =
    product.includes("plan") ||
    product.includes("typical") ||
    product.includes("entrepreneur") ||
    product.includes("elite") ||
    obj.includes("subscribe") ||
    obj.includes("subscription") ||
    obj.includes("trial") ||
    obj.includes("signup") ||
    obj.includes("sign up") ||
    terms.includes("subscription");

  const base = {
    qr_type,
    title: title || "",
    objective: objRaw,
    objective_summary: objRaw || `Assist user with ${qr_type} QR`,
    payload: p,

    // generic flags Paco can reason from
    flow: qr_type,
    action: "assist",
    urgency: "normal",
    lead_intent: "unknown",
    shouldCreateClient: false,
    shouldCreateLead: true,
    shouldOfferPaymentLink: false,
    shouldCollectContactInfo: false,
    shouldSchedule: false,
    shouldCreateOrder: false,
    shouldEscalateHuman: false,

    collectFields: [],
    product_name: p.product_name || "",
    plan: p.plan_name || p.product_name || "",
    billing: p.plan_term || p.billing || "monthly",
    discount: p.discount || "",
    promo_code: p.promo_code || "",
    trial_days: p.trial_days || "",
    terms: p.terms || ""
  };

  if (qr_type === "storefront") {
    return {
      ...base,
      flow: "storefront",
      action: "enter_storefront",
      urgency: "normal",
      lead_intent: "browse_store",
      shouldCollectContactInfo: false
    };
  }

  if (qr_type === "product") {
    return {
      ...base,
      flow: "commerce",
      action: "view_product",
      lead_intent: "product_inquiry_or_buy",
      shouldCreateOrder: true,
      shouldCollectContactInfo: true,
      collectFields: ["name", "phone"]
    };
  }

  if (qr_type === "trial") {
    return {
      ...base,
      flow: "subscription",
      action: "start_trial",
      urgency: "high",
      lead_intent: "trial_signup",
      shouldCreateClient: true,
      shouldOfferPaymentLink: true,
      shouldCollectContactInfo: true,
      collectFields: [
        "business_name",
        "business_description",
        "owner_name",
        "email",
        "phone",
        "city_state_or_location"
      ]
    };
  }

  if (qr_type === "promo" && hasSubscriptionSignal) {
    return {
      ...base,
      flow: "subscription",
      action: "promo_subscription",
      urgency: "high",
      lead_intent: "discount_signup",
      shouldCreateClient: true,
      shouldOfferPaymentLink: true,
      shouldCollectContactInfo: true,
      collectFields: [
        "business_name",
        "business_description",
        "owner_name",
        "email",
        "phone",
        "city_state_or_location"
      ]
    };
  }

  if (qr_type === "promo") {
    return {
      ...base,
      flow: "sales",
      action: "redeem_promo",
      lead_intent: "buy_with_discount",
      shouldCollectContactInfo: true,
      collectFields: ["name", "phone"]
    };
  }

  if (qr_type === "service") {
    return {
      ...base,
      flow: "appointment",
      action: "book_service",
      lead_intent: "service_booking",
      shouldSchedule: true,
      shouldCollectContactInfo: true,
      collectFields: ["name", "phone", "preferred_date", "preferred_time"]
    };
  }

  if (qr_type === "order") {
    return {
      ...base,
      flow: "commerce",
      action: "create_order",
      lead_intent: "place_order",
      shouldCreateOrder: true,
      shouldCollectContactInfo: true,
      collectFields: ["name", "phone", "delivery_or_pickup", "address"]
    };
  }

  if (qr_type === "event") {
    return {
      ...base,
      flow: "event",
      action: "event_ticket_or_registration",
      lead_intent: "event_interest",
      shouldCollectContactInfo: true,
      collectFields: ["name", "phone", "email", "ticket_quantity"]
    };
  }

  return base;
}

// ✅ ES Module named exports for React / Webpack
export {
  qrSchemas,
  qrFieldLabels,
  getQrFieldLabel,
  validatePayload,
  deriveQrIntent
};

export default qrSchemas;